import { Color, Vector3 } from "three";
import { config, getFullTimelineLength } from "./config";
import AtlasPlanes from "./AtlasPlanes.js";
import { chance, random, TO_RAD, weightedRandom } from "~/lib/utils";
import EaseNumber from "~/lib/EaseNumber.js";
import { about, PHRASE } from "./index.js";
import colors from "./colors.js";

const INCRESE_SPEED = 1.01;
const CHARGING_SPEED = 1.01;
const DECRESE_SPEED = 0.98;
const MIN_VISIBLE = 0.05;

export default class Lines extends AtlasPlanes {
    preInit() {
        this.texturePath = "/about/lineTextures.png";
        this.planeCount = config.lines.count;
        this.shapeCount = 4;
        this.instancing = config.instancing;
        this.direction = new EaseNumber(1);

        this.near = getFullTimelineLength();
        this.far = -config.camera.far;

        this.initScale = new Vector3(
            config.lines.defaultWidth,
            config.lines.defaultLength,
            1,
        );

        this._bindEvents();
    }

    initPlane(plane, overwriteZ = null) {
        plane.isReversed = chance(0.1);
        plane.direction = plane.isReversed ? -1 : 1;
        plane.color = new Color(colors.orange);

        if (
            about.phrase !== PHRASE.ENDING &&
            about.phrase !== PHRASE.BOOSTING
        ) {
            this._rescale(plane);
        }
        this._reposition(plane, overwriteZ);

        plane.speed = weightedRandom({
            [random(1, 5)]: 1,
            20: 0.01,
        });

        if (plane.isReversed) {
            plane.speed *= 20;
        }
    }

    syncDefaultSizeConfigs() {
        this.initScale.set(
            config.lines.defaultWidth,
            config.lines.defaultLength,
            1,
        );
        this.planes.forEach((plane) => {
            this._rescale(plane);
        });
    }

    syncScaleConfigs() {
        this.planes.forEach((plane) => {
            this._rescale(plane);
        });
    }

    syncPositionConfigs() {
        this.planes.forEach((plane) => {
            this._reposition(plane);
        });
    }

    update(progress) {
        super.update(progress);

        this.count = Math.min(3000, config.lines.count + ~~(progress * 2500));

        switch (about.phrase) {
            case PHRASE.STARTING:
                this.phraseSpeed = 1 + progress * 10;
                break;
            case PHRASE.CHARGING:
                this.phraseSpeed = 0;
                break;
            case PHRASE.BOOSTING:
                this.phraseSpeed = 1 + progress * 50;
                break;
            case PHRASE.ENDING:
                this.phraseSpeed = 1;
                break;
        }
    }

    updatePlane(plane, progress) {
        if (!plane.initScale) {
            // workaround: ending -> boosting issue
            this._rescale(plane);
        }

        this._move(plane);
        this._loopPosition(plane);

        switch (about.phrase) {
            case PHRASE.STARTING:
                plane.visibleInShader = true;
                this._updatePlaneInStartPhrase(plane, progress);
                break;
            case PHRASE.CHARGING:
                plane.visibleInShader = true;
                this._updatePlaneInChargePhrase(plane, progress);
                break;
            case PHRASE.BOOSTING:
                plane.visibleInShader = true;
                this._updatePlaneInBoostPhrase(plane, progress);
                break;
            case PHRASE.ENDING:
                this._fadeOut(plane);
                break;
        }
    }

    destroy() {
        super.destroy();
        this.direction.destroy();
        about.off("phraseChanged", this._onPhraseChanged);
    }

    _bindEvents() {
        this._onPhraseChanged = this._onPhraseChanged.bind(this);
        about.on("phraseChanged", this._onPhraseChanged);
    }

    _onPhraseChanged(prev, next) {
        if (prev === PHRASE.ENDING && next === PHRASE.BOOSTING) {
            this.planes.forEach((plane) => {
                this._rescale(plane);
            });
        }
    }

    _updatePlaneInStartPhrase(plane, progress) {
        if (plane.scale.y > plane.initScale.y) {
            plane.scale.y *= DECRESE_SPEED;
        } else if (plane.scale.y <= plane.initScale.y) {
            plane.scale.y *= INCRESE_SPEED;
        }

        if (plane.scale.x <= plane.initScale.x) {
            plane.scale.x *= INCRESE_SPEED;
        }
    }

    _updatePlaneInChargePhrase(plane, progress) {
        if (plane.scale.y <= plane.initScale.y * 20) {
            plane.scale.y *= CHARGING_SPEED;
        }
    }

    _updatePlaneInBoostPhrase(plane, progress) {
        if (plane.scale.y <= plane.initScale.y * 10) {
            plane.scale.y *= INCRESE_SPEED;
        }
    }

    _move(plane) {
        plane.position.z +=
            plane.speed *
            plane.direction *
            config.lines.baseSpeed *
            this.phraseSpeed *
            this.direction.value;
    }

    _fadeOut(plane) {
        if (plane.scale.y <= MIN_VISIBLE && plane.scale.x <= MIN_VISIBLE) {
            plane.visibleInShader = false;
            return;
        }

        if (plane.scale.y > MIN_VISIBLE) {
            plane.scale.y *= DECRESE_SPEED;
        }
        if (plane.scale.x > MIN_VISIBLE) {
            plane.scale.x *= DECRESE_SPEED;
        }
    }

    _loopPosition(plane) {
        if (plane.position.z > this.near) {
            this.initPlane(plane, this.far);
        }

        if (plane.position.z < this.far) {
            this.initPlane(plane, this.near);
        }
    }

    _rescale(plane) {
        if (!plane.initScale) {
            plane.initScale = this.initScale.clone();
        } else {
            plane.initScale.copy(this.initScale);
        }

        plane.initScale.x *= +weightedRandom({
            [0.3]: config.lines.sizeChances.thin,
            [0.8]: config.lines.sizeChances.medium,
            [1.2]: config.lines.sizeChances.thick,
        });

        // if (plane.isReversed) {
        //   plane.initScale.x = .5;
        //   plane.initScale.y = this.initScale.y * 10;
        // }

        plane.scale.copy(plane.initScale);
    }

    _reposition(plane, overwriteZ = null) {
        const angle = random(360) * TO_RAD;
        const r = config.lines.radius + random(config.lines.radiusRandomRange);

        plane.rotation.z = 0;
        plane.rotation.x = 90 * TO_RAD;
        plane.rotation.y = angle - 90 * TO_RAD;

        if (config.lines.randomDirection !== undefined) {
            plane.rotation.x +=
                random(-1, 1) * TO_RAD * config.lines.randomDirection;
            plane.rotation.z +=
                random(-1, 1) * TO_RAD * config.lines.randomDirection;
        }

        plane.position.x = r * Math.cos(angle);
        plane.position.y = r * Math.sin(angle);
        plane.position.z = overwriteZ || random(this.far, this.near * 0.8);
    }
}
